import React, { useEffect, useState } from "react";
import {
  AutoComplete, Button, Checkbox, Drawer, Form, Input, InputNumber, Radio, Switch, message,
} from "antd";

import * as api from "./controller";
import LeadTagPicker from "./LeadTagPicker";
import "./leads.css";

// The same list the website form offers, so a lead typed in by hand and one
// that arrived from the form spell the PMS the same way. Free text is still
// allowed — "Other" exists because the list is never complete.
export const PMS_SUGGESTIONS = [
  "Dentrix", "Open Dental", "Curve Hero", "Tracker", "ClearDent", "Eaglesoft",
  "Power Practice", "ABELDent", "Dentitek", "Paradigm", "MaxiDent", "Other",
];

// Value = what the server stores in tasks_wanted; label = the website's wording.
export const TASK_OPTIONS = [
  { value: "breakdowns", label: "Insurance breakdowns based on schedule" },
  { value: "estimates", label: "Insurance estimate tracking" },
  { value: "claims", label: "Insurance claims tracking" },
  { value: "other", label: "Anything other than the above" },
];

// The three free-text questions, verbatim from the website form. The detail
// drawer shows the same wording above each answer so what the customer read
// and what we read are the same sentence.
export const QUESTIONS = {
  current_workflow: "What is your current workflow for insurance verification (breakdowns and estimates)?",
  breakdown_recording: "How is insurance breakdown information recorded in your PMS or in paper records?",
  team_involved: "How many and which team members are involved in insurance coordination work?",
};

// Form.Item hands a field {value, onChange}; the picker speaks {tagIds, newTags}.
// Keep the whole object as the field value so new names typed into the picker
// survive until save, when they become new_tags.
function TagsField({ value, onChange }) {
  const v = value || { tagIds: [], newTags: [] };
  return (
    <LeadTagPicker
      value={v.tagIds}
      onChange={(next) => onChange({ tagIds: next.tagIds || [], newTags: next.newTags || [] })}
      placeholder="Add tags"
      style={{ width: "100%" }}
    />
  );
}

function principalOf(lead) {
  return ((lead && lead.contacts) || []).find((c) => c.role === "principal_dentist") || null;
}

function toFields(lead) {
  const p = principalOf(lead) || {};
  return {
    practice_name: lead.practice_name || "",
    website: lead.website || "",
    is_multi_clinic: !!lead.is_multi_clinic,
    clinic_count: lead.clinic_count || null,
    pms: lead.pms || "",
    principal_name: p.name || "",
    principal_email: p.email || "",
    principal_phone: p.phone || "",
    email: lead.email || "",
    phone: lead.phone || "",
    address: lead.address || "",
    city: lead.city || "",
    state: lead.state || "",
    zip_code: lead.zip_code || "",
    frontdesk_staff_count: lead.frontdesk_staff_count == null ? null : lead.frontdesk_staff_count,
    insurance_coordinator_count: lead.insurance_coordinator_count == null ? null : lead.insurance_coordinator_count,
    treatment_coordinator_count: lead.treatment_coordinator_count == null ? null : lead.treatment_coordinator_count,
    tasks_wanted: Array.isArray(lead.tasks_wanted) ? lead.tasks_wanted : [],
    tasks_other: lead.tasks_other || "",
    current_workflow: lead.current_workflow || "",
    breakdown_recording: lead.breakdown_recording || "",
    team_involved: lead.team_involved || "",
    communication_method: lead.communication_method || undefined,
    tags: { tagIds: (lead.tags || []).map((t) => t.id), newTags: [] },
    send_booking_email: false,
  };
}

const EMPTY = toFields({});

function SectionTitle({ children }) {
  return <div className="crml-form-section-title">{children}</div>;
}

/**
 * Create / edit a lead. One form, sectioned the way the website form is, so an
 * admin taking the same details over the phone walks the same order.
 *
 * `lead` null = create. On create only, "Email them the booking link now" asks
 * the server to send the booking email as part of the same request; the
 * response's `booking_email` says whether it went, and the toast says so too —
 * a lead silently created without the email it promised is a lost follow-up.
 */
export default function LeadFormDrawer({ open, lead, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const isEdit = !!(lead && lead.id);

  // The form instance outlives the drawer body (destroyOnClose unmounts the
  // fields, not the store), so reset before seeding or a previous lead's
  // touched values could leak into the next one.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(lead ? toFields(lead) : EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead && lead.id]);

  const submit = async (v) => {
    const principal = {
      name: (v.principal_name || "").trim(),
      email: (v.principal_email || "").trim(),
      phone: (v.principal_phone || "").trim(),
    };
    const hadPrincipal = !!principalOf(lead);
    const tags = v.tags || { tagIds: [], newTags: [] };
    const body = {
      practice_name: (v.practice_name || "").trim(),
      website: (v.website || "").trim(),
      is_multi_clinic: !!v.is_multi_clinic,
      clinic_count: v.is_multi_clinic ? v.clinic_count || null : null,
      pms: (v.pms || "").trim(),
      email: (v.email || "").trim(),
      phone: (v.phone || "").trim(),
      address: (v.address || "").trim(),
      city: (v.city || "").trim(),
      state: (v.state || "").trim(),
      zip_code: (v.zip_code || "").trim(),
      // No country field in the form: the server default stands on create and
      // whatever the lead already has stands on edit.
      frontdesk_staff_count: v.frontdesk_staff_count == null ? null : v.frontdesk_staff_count,
      insurance_coordinator_count: v.insurance_coordinator_count == null ? null : v.insurance_coordinator_count,
      treatment_coordinator_count: v.treatment_coordinator_count == null ? null : v.treatment_coordinator_count,
      tasks_wanted: v.tasks_wanted || [],
      tasks_other: (v.tasks_wanted || []).includes("other") ? (v.tasks_other || "").trim() : "",
      current_workflow: (v.current_workflow || "").trim(),
      breakdown_recording: (v.breakdown_recording || "").trim(),
      team_involved: (v.team_involved || "").trim(),
      communication_method: v.communication_method || "",
      tag_ids: tags.tagIds || [],
      new_tags: tags.newTags || [],
    };
    // Only send the principal when there is one to write (or one to clear on
    // edit) — an empty object would make the server create a nameless contact.
    if (principal.name || principal.email || principal.phone || hadPrincipal) {
      body.principal_dentist = principal;
    }
    if (!isEdit && v.send_booking_email) body.send_booking_email = true;

    setBusy(true);
    const r = isEdit ? await api.updateLead(lead.id, body) : await api.createLead(body);
    setBusy(false);
    if (!(r.status >= 200 && r.status < 300)) {
      message.error((r.json && r.json.detail) || (isEdit ? "Couldn't save the lead." : "Couldn't create the lead."));
      return;
    }
    const saved = r.json || {};
    if (isEdit) {
      message.success("Lead saved.");
    } else if (saved.booking_email) {
      const be = saved.booking_email;
      if (be.status === "sent") {
        message.success("Lead created and booking email sent.");
      } else {
        message.warning(`Lead created — email not sent: ${be.detail || be.status}`);
      }
    } else {
      message.success("Lead created.");
    }
    onSaved && onSaved(saved);
    onClose();
  };

  return (
    <Drawer
      title={isEdit ? `Edit lead — ${lead.practice_name || lead.email || ""}` : "New lead"}
      width={640}
      open={open}
      onClose={onClose}
      destroyOnClose
      // Above a detail drawer when opened from one; the app pins open drawers at
      // 10001 in CSS, so ordering between two comes down to DOM order anyway.
      zIndex={10002}
      footer={
        <div className="crml-form-foot">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={busy} onClick={() => form.submit()}>
            {isEdit ? "Save" : "Create lead"}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="crml-form" initialValues={EMPTY} onFinish={submit} requiredMark={false}>
        {/* ── Practice ─────────────────────────────────────────── */}
        <div className="crml-form-section">
          <SectionTitle>Practice</SectionTitle>
          <div className="crml-form-grid">
            <Form.Item
              className="span2"
              name="practice_name"
              label="Practice name"
              dependencies={["email"]}
              rules={[
                ({ getFieldValue }) => ({
                  // Mirrors the server: a lead needs something to be found by —
                  // a name or an email address, either will do.
                  validator: (_, value) =>
                    (value && value.trim()) || (getFieldValue("email") || "").trim()
                      ? Promise.resolve()
                      : Promise.reject(new Error("Enter a practice name or a clinic email.")),
                }),
              ]}
            >
              <Input placeholder="e.g. Maple Dental" />
            </Form.Item>
            <Form.Item name="website" label="Website">
              <Input placeholder="https://" />
            </Form.Item>
            <Form.Item name="pms" label="Practice management system">
              <AutoComplete
                options={PMS_SUGGESTIONS.map((p) => ({ value: p }))}
                filterOption={(text, opt) => opt.value.toLowerCase().includes((text || "").toLowerCase())}
                placeholder="Dentrix, Open Dental, …"
                dropdownStyle={{ zIndex: 10060 }}
              />
            </Form.Item>
            <Form.Item name="is_multi_clinic" label="More than one clinic" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.is_multi_clinic !== b.is_multi_clinic}>
              {({ getFieldValue }) =>
                getFieldValue("is_multi_clinic") ? (
                  <Form.Item name="clinic_count" label="How many clinics">
                    <InputNumber min={2} style={{ width: "100%" }} />
                  </Form.Item>
                ) : (
                  <div />
                )
              }
            </Form.Item>
          </div>
        </div>

        {/* ── Principal dentist ────────────────────────────────── */}
        <div className="crml-form-section">
          <SectionTitle>Principal dentist</SectionTitle>
          <div className="crml-form-grid">
            <Form.Item className="span2" name="principal_name" label="Name">
              <Input placeholder="Dr …" />
            </Form.Item>
            <Form.Item name="principal_email" label="Email" rules={[{ type: "email", message: "That doesn't look like an email address." }]}>
              <Input type="email" />
            </Form.Item>
            <Form.Item name="principal_phone" label="Phone">
              <Input />
            </Form.Item>
          </div>
        </div>

        {/* ── Clinic contact ───────────────────────────────────── */}
        <div className="crml-form-section">
          <SectionTitle>Clinic contact</SectionTitle>
          <div className="crml-form-grid">
            <Form.Item
              name="email"
              label="Clinic email"
              dependencies={["practice_name"]}
              rules={[{ type: "email", message: "That doesn't look like an email address." }]}
            >
              <Input type="email" />
            </Form.Item>
            <Form.Item name="phone" label="Clinic phone">
              <Input />
            </Form.Item>
            <Form.Item className="span2" name="address" label="Address">
              <Input />
            </Form.Item>
            <Form.Item name="city" label="City">
              <Input />
            </Form.Item>
            <Form.Item name="state" label="Province">
              <Input />
            </Form.Item>
            <Form.Item name="zip_code" label="Postal code">
              <Input />
            </Form.Item>
          </div>
        </div>

        {/* ── Team ─────────────────────────────────────────────── */}
        <div className="crml-form-section">
          <SectionTitle>Team</SectionTitle>
          <div className="crml-form-grid">
            <Form.Item name="frontdesk_staff_count" label="Front desk staff">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="insurance_coordinator_count" label="Insurance coordinators">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="treatment_coordinator_count" label="Treatment coordinators">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </div>
        </div>

        {/* ── What they need ───────────────────────────────────── */}
        <div className="crml-form-section">
          <SectionTitle>What they need</SectionTitle>
          <Form.Item name="tasks_wanted" label="Which tasks would you like handled?">
            <Checkbox.Group options={TASK_OPTIONS} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.tasks_wanted !== b.tasks_wanted}>
            {({ getFieldValue }) =>
              (getFieldValue("tasks_wanted") || []).includes("other") ? (
                <Form.Item name="tasks_other" label="Tell us what else">
                  <Input.TextArea rows={2} autoSize={{ minRows: 2, maxRows: 5 }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="current_workflow" label={QUESTIONS.current_workflow}>
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
          <Form.Item name="breakdown_recording" label={QUESTIONS.breakdown_recording}>
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
          <Form.Item name="team_involved" label={QUESTIONS.team_involved}>
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
          <Form.Item name="communication_method" label="Best communication method for your team">
            <Radio.Group>
              <Radio value="email">Email</Radio>
              <Radio value="whatsapp">WhatsApp</Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        {/* ── Tags ─────────────────────────────────────────────── */}
        <div className="crml-form-section">
          <SectionTitle>Tags</SectionTitle>
          <Form.Item name="tags" label="Tags">
            <TagsField />
          </Form.Item>
          <div className="crml-form-hint">Type a name that isn't in the list to create a new tag when you save.</div>
        </div>

        {!isEdit ? (
          <div className="crml-form-section">
            <Form.Item name="send_booking_email" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Email them the booking link now</Checkbox>
            </Form.Item>
            <div className="crml-form-hint" style={{ marginTop: 4 }}>
              Uses the booking template from CRM Settings › Leads. You can also send it later from the lead.
            </div>
          </div>
        ) : null}
      </Form>
    </Drawer>
  );
}
